import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import { Box, Chip, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { alpha } from '@mui/material/styles';
import { getDetectedSoilingTypes } from '../../constants/persistentSoiling';
import { useFieldValue } from '../../hooks/useFormValues';
import type { FieldComponentProps } from '../form-engine/types';

export const RECOMMENDATIONS_ANCHOR_ID = 'persistent-soiling-recommendations';

// Lines starting with "-" become bullet points; consecutive ones are grouped into one list.
const RecommendationText = ({ text }: { text: string }) => {
  const blocks: (string | string[])[] = [];
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      if (!line.startsWith('-')) {
        blocks.push(line);
        return;
      }
      const item = line.replace(/^-\s*/, '');
      const last = blocks[blocks.length - 1];
      if (Array.isArray(last)) {
        last.push(item);
      } else {
        blocks.push([item]);
      }
    });

  return blocks.map((block, index) =>
    Array.isArray(block) ? (
      <Box component="ul" key={index} sx={{ pl: 2.5, my: 1 }}>
        {block.map((item, itemIndex) => (
          <Typography component="li" variant="body2" color="text.primary" key={itemIndex} sx={{ mb: 0.75 }}>
            {item}
          </Typography>
        ))}
      </Box>
    ) : (
      <Typography key={index} variant="body2" color="text.primary" sx={{ my: 1 }}>
        {block}
      </Typography>
    ),
  );
};

const EmptyState = ({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 4, gap: 1 }}>
    <Box
      sx={(theme) => ({
        display: 'flex',
        p: 1.5,
        borderRadius: '50%',
        color: 'primary.main',
        bgcolor: alpha(theme.palette.primary.main, 0.1),
      })}
    >
      {icon}
    </Box>
    <Typography variant="subtitle1" color="text.primary" sx={{ fontWeight: 600 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {text}
    </Typography>
  </Box>
);

const PersistentSoilingRecommendations = ({ field }: FieldComponentProps) => {
  const answers = useFieldValue(field.name!) || {};
  const hasAnswers = Object.values(answers).some(Boolean);
  const detected = getDetectedSoilingTypes(answers);

  return (
    <Grid size={12} id={RECOMMENDATIONS_ANCHOR_ID} sx={{ scrollMarginTop: 160 }}>
      {!hasAnswers ? (
        <EmptyState icon={<QuizOutlinedIcon />} title="No answers yet" text="Answer the questions above to get tailored recommendations." />
      ) : detected.length === 0 ? (
        <EmptyState
          icon={<CheckCircleOutlineIcon />}
          title="No persistent soiling risk detected"
          text="Based on the current answers, no soiling type reaches the risk threshold."
        />
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {detected.length} soiling {detected.length === 1 ? 'type' : 'types'} with a detected risk, based on the current answers.
          </Typography>
          {detected.map((soilingType) => (
            <Box
              key={soilingType.key}
              sx={{ mb: 2, p: 2, border: 1, borderColor: 'divider', borderLeft: 4, borderLeftColor: 'error.main', borderRadius: 1 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 0.5 }}>
                <Typography variant="h6" color="text.primary">
                  {soilingType.label}
                </Typography>
                <Chip
                  size="small"
                  icon={<FiberManualRecordIcon />}
                  label="Soiling risk detected"
                  sx={(theme) => ({
                    fontWeight: 600,
                    color: 'error.main',
                    bgcolor: alpha(theme.palette.error.main, 0.1),
                    '& .MuiChip-icon': { color: 'error.main', fontSize: 10 },
                  })}
                />
              </Box>
              <RecommendationText text={soilingType.recommendation} />
            </Box>
          ))}
        </>
      )}
    </Grid>
  );
};

export default PersistentSoilingRecommendations;
